import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { Tag } from 'primereact/tag';
import { toast } from 'react-toastify';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePostRequest } from '../../service/PostTemplate';
import { handlePatchRequest } from '../../service/PatchTemplate';
import { endPoints } from '../../Config';
import { formatTimeFromMinutes } from '../../utilities/formatTime';
import { getSyncroModuleOptions } from '../../navigation/syncroNavigation';
import { translateUiText } from '../../utilities/siteTranslations';
import PhotoUploader from '../../components/PhotoUploader';
import { getPreferredLanguage } from '../../utilities/languagePreference';
import { translateHrText, translateHrToken } from './hrTranslations';

const feedbackTypeOptions = [
    { label: 'Complaint', value: 'complaint' },
    { label: 'Recognition', value: 'recognition' }
];

const requestStatusOptions = [
    { label: 'Open', value: 'open' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Resolved', value: 'resolved' }
];

const requestPriorityOptions = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' }
];

const feedbackStatusOptions = [
    { label: 'Open', value: 'open' },
    { label: 'In Review', value: 'in_review' },
    { label: 'Resolved', value: 'resolved' }
];

const severityOptions = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' }
];
const MAX_HR_PHOTOS = 10;
const SYNCRO_REQUEST_CATEGORY = '💻 Syncro — Support & Development';

const normalizeText = (value) => String(value || '').trim().toLowerCase();
const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '-');
const formatUserName = (user) => `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || '-';
const calculateElapsedMinutes = (createdAt, closedAt = null) => {
    if (!createdAt) return null;
    const startedAt = new Date(createdAt);
    const endedAt = closedAt ? new Date(closedAt) : new Date();
    return Math.max(0, Math.floor((endedAt - startedAt) / (1000 * 60)));
};
const getElapsedColor = (minutes) => {
    if (minutes === null) return null;
    if (minutes < 120) return '#22c55e';
    if (minutes < 480) return '#eab308';
    if (minutes < 1440) return '#f97316';
    return '#ef4444';
};

const statusSeverity = (status) => {
    const value = String(status || '').toLowerCase();
    if (value === 'resolved' || value === 'recognition' || value === 'completed') return 'success';
    if (value === 'in_progress' || value === 'in_review' || value === 'medium') return 'warning';
    if (value === 'high' || value === 'complaint') return 'danger';
    return 'info';
};

const HRModule = () => {
    const dispatch = useDispatch();
    const [preferredLanguage, setPreferredLanguage] = useState(getPreferredLanguage());
    const currentUser = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const currentAdmin = currentUser?.data?.adminUser || {};
    const currentRoleUid = currentAdmin?.role?.uid;
    const isAdmin = currentRoleUid === 'admin' || currentRoleUid === 'superAdmin';

    const [moduleConfig, setModuleConfig] = useState({ entryOptions: [], requestCategories: [], feedbackCategories: {}, performanceReview: {} });
    const [requests, setRequests] = useState([]);
    const [feedbackLogs, setFeedbackLogs] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [machines, setMachines] = useState([]);
    const [routingSettings, setRoutingSettings] = useState({ requests: [], feedback: [] });
    const [activeTab, setActiveTab] = useState(0);
    const [showEntryDialog, setShowEntryDialog] = useState(false);
    const [entryMode, setEntryMode] = useState('request');
    const [requestSearch, setRequestSearch] = useState('');
    const [feedbackSearch, setFeedbackSearch] = useState('');
    const [employeeSearch, setEmployeeSearch] = useState('');
    const [requestFilters, setRequestFilters] = useState({
        status: null,
        priority: null,
        category: null,
        routingKey: null
    });
    const [feedbackFilters, setFeedbackFilters] = useState({
        type: null,
        status: null,
        severity: null,
        targetEmployee: null,
        routingKey: null
    });
    const [employeeFilters, setEmployeeFilters] = useState({
        role: null,
        team: null,
        alertsOnly: false
    });
    const [profileDialog, setProfileDialog] = useState(false);
    const [employeeProfile, setEmployeeProfile] = useState(null);
    const [detailDialog, setDetailDialog] = useState({ visible: false, type: 'request', data: null });
    const [savingModule, setSavingModule] = useState(false);
    const [detailComment, setDetailComment] = useState('');
    const [loadingProfileId, setLoadingProfileId] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [savingRoutingKey, setSavingRoutingKey] = useState('');
    const [requestRoutingCategoryFilter, setRequestRoutingCategoryFilter] = useState(null);
    const [requestRoutingSearch, setRequestRoutingSearch] = useState('');
    const [routedStatusFilter, setRoutedStatusFilter] = useState(null);
    const [routedSortOrder, setRoutedSortOrder] = useState('newest');
    const [entryForm, setEntryForm] = useState({
        category: '',
        subcategory: '',
        syncroModule: null,
        description: '',
        location: '',
        machineId: '',
        photoUrls: [],
        priority: 'medium',
        assignedTo: '',
        feedbackType: 'complaint',
        feedbackCategory: '',
        targetEmployee: '',
        severity: 'medium',
        isConfidentialSubmission: false
    });

    const isOperator = currentRoleUid === 'operator';

    const loadModuleData = useCallback(async () => {
        const machineEndpoint = isOperator
            ? `${endPoints.getallMachine}?assignedOnly=true`
            : endPoints.getallMachine;

        if (isAdmin) {
            const [configResponse, requestsResponse, feedbackResponse, employeesResponse, machineResponse, routingResponse] = await Promise.all([
                handleGetRequest(endPoints.hrCategories, false, false, dispatch),
                handleGetRequest(endPoints.hrRequests, false, false, dispatch),
                handleGetRequest(endPoints.hrFeedback, false, false, dispatch),
                handleGetRequest(endPoints.hrEmployees, false, false, dispatch),
                handleGetRequest(machineEndpoint, false, false, dispatch),
                handleGetRequest(endPoints.hrRoutingSettings, false, false, dispatch)
            ]);

            setModuleConfig(configResponse?.data || { entryOptions: [], requestCategories: [], feedbackCategories: {}, performanceReview: {} });
            setRequests(requestsResponse?.data || []);
            setFeedbackLogs(feedbackResponse?.data || []);
            setEmployees(employeesResponse?.data || []);
            setMachines(machineResponse?.data || []);
            setRoutingSettings(routingResponse?.data || { requests: [], feedback: [] });
        } else {
            const [configResponse, requestsResponse, feedbackResponse, machineResponse, usersResponse] = await Promise.all([
                handleGetRequest(endPoints.hrCategories, false, false, dispatch),
                handleGetRequest(endPoints.hrRequests, false, false, dispatch),
                handleGetRequest(endPoints.hrFeedback, false, false, dispatch),
                handleGetRequest(machineEndpoint, false, false, dispatch),
                handleGetRequest(endPoints.getallUsers, false, false, dispatch)
            ]);

            setModuleConfig(configResponse?.data || { entryOptions: [], requestCategories: [], feedbackCategories: {}, performanceReview: {} });
            setRequests(requestsResponse?.data || []);
            setFeedbackLogs(feedbackResponse?.data || []);
            setMachines(machineResponse?.data || []);
            // Build employee-like list from users for dropdowns
            const userList = (usersResponse?.data || []).map((u) => ({
                _id: u._id,
                firstName: u.firstName,
                lastName: u.lastName,
                email: u.email,
                role: u.role,
                team: u.teamId
            }));
            setEmployees(userList);
        }
    }, [dispatch, isAdmin, isOperator]);

    useEffect(() => {
        loadModuleData();
    }, [loadModuleData]);

    useEffect(() => {
        const syncPreferredLanguage = () => setPreferredLanguage(getPreferredLanguage());
        window.addEventListener('preferred-language-changed', syncPreferredLanguage);
        window.addEventListener('storage', syncPreferredLanguage);

        return () => {
            window.removeEventListener('preferred-language-changed', syncPreferredLanguage);
            window.removeEventListener('storage', syncPreferredLanguage);
        };
    }, []);

    const t = useCallback((value) => translateHrText(value, preferredLanguage), [preferredLanguage]);

    const syncroModuleOptions = useMemo(
        () => getSyncroModuleOptions((value) => translateUiText(value, preferredLanguage)),
        [preferredLanguage]
    );

    const requestCategoryOptions = useMemo(
        () => (moduleConfig?.requestCategories || []).map((item) => ({ label: t(item.label), value: item.label })),
        [moduleConfig, t]
    );

    const selectedRequestCategory = useMemo(
        () => (moduleConfig?.requestCategories || []).find((item) => item.label === entryForm.category),
        [moduleConfig, entryForm.category]
    );
    const isSyncroRequest = entryForm.category === SYNCRO_REQUEST_CATEGORY;

    const requestSubcategoryOptions = useMemo(
        () => (selectedRequestCategory?.subcategories || []).map((item) => ({ label: t(item), value: item })),
        [selectedRequestCategory, t]
    );

    const feedbackCategoryOptions = useMemo(() => {
        const list = moduleConfig?.feedbackCategories?.[entryForm.feedbackType] || [];
        return list.map((item) => ({ label: t(item), value: item }));
    }, [moduleConfig, entryForm.feedbackType, t]);

    const localizedFeedbackTypeOptions = useMemo(
        () => feedbackTypeOptions.map((item) => ({ ...item, label: t(item.label) })),
        [t]
    );

    const localizedRequestStatusOptions = useMemo(
        () => requestStatusOptions.map((item) => ({ ...item, label: t(item.label) })),
        [t]
    );

    const localizedRequestPriorityOptions = useMemo(
        () => requestPriorityOptions.map((item) => ({ ...item, label: t(item.label) })),
        [t]
    );

    const localizedFeedbackStatusOptions = useMemo(
        () => feedbackStatusOptions.map((item) => ({ ...item, label: t(item.label) })),
        [t]
    );

    const localizedSeverityOptions = useMemo(
        () => severityOptions.map((item) => ({ ...item, label: t(item.label) })),
        [t]
    );

    const employeeOptions = useMemo(
        () =>
            employees.map((item) => ({
                label: `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email,
                value: item._id
            })),
        [employees]
    );

    const assigneeOptions = useMemo(
        () =>
            employees
                .filter((item) => item?.role?.uid !== 'operator')
                .map((item) => ({
                    label: `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email,
                    value: item._id
                })),
        [employees]
    );

    const machineOptions = useMemo(
        () =>
            machines.map((item) => ({
                label: [
                    item.machine || item.name || t('Unnamed Machine'),
                    item.type || t('Unknown Type'),
                    item.location || t('No location'),
                    item.serialNumber || item.deviceId || item.uuid || t('No ID')
                ].join(' - '),
                value: item._id
            })),
        [machines, t]
    );

    const requestRoutingOptions = useMemo(() => {
        const rules = moduleConfig?.requestRoutingRules || [];
        const configured = rules.map((item) => ({ label: item.routingLabel, value: item.routingKey }));
        const seen = new Set(configured.map((item) => item.value));
        const discovered = requests
            .filter((item) => item?.routingKey && !seen.has(item.routingKey))
            .map((item) => ({ label: t(item.routingLabel || item.routingKey), value: item.routingKey }));
        return [...configured.map((item) => ({ ...item, label: t(item.label) })), ...discovered].sort((a, b) => a.label.localeCompare(b.label));
    }, [moduleConfig, requests, t]);

    const feedbackRoutingOptions = useMemo(() => {
        const rules = moduleConfig?.feedbackRoutingRules || {};
        return Object.values(rules)
            .map((item) => ({ label: t(item.routingLabel), value: item.routingKey }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [moduleConfig, t]);

    const employeeRoleOptions = useMemo(() => {
        const unique = Array.from(new Set(employees.map((item) => item?.role?.uid).filter(Boolean)));
        return unique.map((value) => ({
            label: employees.find((item) => item?.role?.uid === value)?.role?.title || value,
            value
        }));
    }, [employees]);

    const employeeTeamOptions = useMemo(() => {
        const unique = Array.from(new Set(employees.map((item) => item?.team?._id || item?.team?.name || item?.team?.title).filter(Boolean)));
        return unique.map((value) => {
            const match = employees.find((item) => (item?.team?._id || item?.team?.name || item?.team?.title) === value);
            return {
                label: match?.team?.name || match?.team?.title || value,
                value
            };
        });
    }, [employees]);

    const selectedRequestRouting = useMemo(() => {
        if (!entryForm.category || !entryForm.subcategory) return null;
        const matchedRule = (moduleConfig?.requestRoutingRules || []).find((item) => item.subcategory === entryForm.subcategory);
        if (matchedRule) return matchedRule.routingLabel;
        return entryForm.category;
    }, [entryForm.category, entryForm.subcategory, moduleConfig]);

    const selectedFeedbackRouting = useMemo(() => {
        if (!entryForm.feedbackType) return null;
        return moduleConfig?.feedbackRoutingRules?.[entryForm.feedbackType]?.routingLabel || null;
    }, [entryForm.feedbackType, moduleConfig]);

    const selectedRequestRoutingSetting = useMemo(
        () =>
            routingSettings.requests.find(
                (item) => item.category === entryForm.category && item.subcategory === entryForm.subcategory
            ) || null,
        [entryForm.category, entryForm.subcategory, routingSettings.requests]
    );

    const selectedRequestAssigneeOptions = useMemo(
        () =>
            (selectedRequestRoutingSetting?.assignedUsers || []).map((item) => ({
                label: formatUserName(item),
                value: item._id
            })),
        [selectedRequestRoutingSetting]
    );

    const requestAssigneeOptionsFor = useCallback(
        (request) => {
            const currentSetting = routingSettings.requests.find(
                (item) => item.category === request?.category && item.subcategory === request?.subcategory
            );
            const owners = currentSetting?.assignedUsers?.length
                ? currentSetting.assignedUsers
                : request?.routeOwners || [];

            return owners.map((item) => ({
                label: formatUserName(item),
                value: item._id || item
            }));
        },
        [routingSettings.requests]
    );

    const selectedFeedbackRoutingSetting = useMemo(
        () =>
            routingSettings.feedback.find(
                (item) => item.feedbackType === entryForm.feedbackType && item.category === entryForm.feedbackCategory
            ) || null,
        [entryForm.feedbackCategory, entryForm.feedbackType, routingSettings.feedback]
    );

    const filteredRequestRoutingSettings = useMemo(() => {
        const search = normalizeText(requestRoutingSearch);
        return (routingSettings.requests || []).filter((item) => {
            if (requestRoutingCategoryFilter && item.category !== requestRoutingCategoryFilter) return false;
            if (!search) return true;

            return [
                t(item.category),
                t(item.subcategory),
                t(item.routingLabel),
                ...(item.assignedUsers || []).map((owner) => formatUserName(owner))
            ]
                .filter(Boolean)
                .some((value) => normalizeText(value).includes(search));
        });
    }, [requestRoutingCategoryFilter, requestRoutingSearch, routingSettings.requests, t]);

    const filteredRequests = useMemo(() => {
        const search = normalizeText(requestSearch);
        return requests.filter((item) => {
            if (requestFilters.status && item?.status !== requestFilters.status) return false;
            if (requestFilters.priority && item?.priority !== requestFilters.priority) return false;
            if (requestFilters.category && item?.category !== requestFilters.category) return false;
            if (requestFilters.routingKey && item?.routingKey !== requestFilters.routingKey) return false;

            if (!search) return true;

            return [
                item.category,
                item.subcategory,
                item.description,
                item.location,
                item.routingLabel,
                item.routeOwners && item.routeOwners.map((owner) => formatUserName(owner)).join(' '),
                item.assignedTo && `${item.assignedTo.firstName || ''} ${item.assignedTo.lastName || ''}`,
                item.requestedFor && `${item.requestedFor.firstName || ''} ${item.requestedFor.lastName || ''}`,
                item.createdBy && `${item.createdBy.firstName || ''} ${item.createdBy.lastName || ''}`
            ]
                .filter(Boolean)
                .some((value) => normalizeText(value).includes(search));
        });
    }, [requestFilters, requestSearch, requests]);

    const filteredFeedback = useMemo(() => {
        const search = normalizeText(feedbackSearch);
        return feedbackLogs.filter((item) => {
            if (feedbackFilters.type && item?.type !== feedbackFilters.type) return false;
            if (feedbackFilters.status && item?.status !== feedbackFilters.status) return false;
            if (feedbackFilters.severity && item?.severity !== feedbackFilters.severity) return false;
            if (feedbackFilters.targetEmployee && item?.targetEmployee?._id !== feedbackFilters.targetEmployee) return false;
            if (feedbackFilters.routingKey && item?.routingKey !== feedbackFilters.routingKey) return false;

            if (!search) return true;

            return [
                item.type,
                item.category,
                item.description,
                item.location,
                item.severity,
                item.routingLabel,
                item.routeOwners && item.routeOwners.map((owner) => formatUserName(owner)).join(' '),
                item.assignedTo && `${item.assignedTo.firstName || ''} ${item.assignedTo.lastName || ''}`,
                item.targetEmployee && `${item.targetEmployee.firstName || ''} ${item.targetEmployee.lastName || ''}`,
                item.createdBy && `${item.createdBy.firstName || ''} ${item.createdBy.lastName || ''}`
            ]
                .filter(Boolean)
                .some((value) => normalizeText(value).includes(search));
        });
    }, [feedbackFilters, feedbackLogs, feedbackSearch]);

    const openRequests = useMemo(() => filteredRequests.filter((item) => item?.status !== 'resolved'), [filteredRequests]);
    const closedRequests = useMemo(() => filteredRequests.filter((item) => item?.status === 'resolved'), [filteredRequests]);
    const openFeedback = useMemo(() => filteredFeedback.filter((item) => item?.status !== 'resolved'), [filteredFeedback]);
    const closedFeedback = useMemo(() => filteredFeedback.filter((item) => item?.status === 'resolved'), [filteredFeedback]);

    const filteredEmployees = useMemo(() => {
        const search = normalizeText(employeeSearch);
        return employees.filter((item) => {
            if (employeeFilters.role && item?.role?.uid !== employeeFilters.role) return false;
            const teamValue = item?.team?._id || item?.team?.name || item?.team?.title || null;
            if (employeeFilters.team && teamValue !== employeeFilters.team) return false;
            if (employeeFilters.alertsOnly && !item?.escalationTriggered) return false;

            if (!search) return true;

            return [
                item.firstName,
                item.lastName,
                item.email,
                item.role?.title,
                item.role?.uid,
                item.team?.name,
                item.team?.title
            ]
                .filter(Boolean)
                .some((value) => normalizeText(value).includes(search));
        });
    }, [employeeFilters, employeeSearch, employees]);

    const summaryCards = useMemo(
        () => [
            {
                label: 'Open Requests',
                value: requests.filter((item) => item.status !== 'resolved').length,
                accent: '#2563eb'
            },
            {
                label: 'Open Feedback',
                value: feedbackLogs.filter((item) => item.status !== 'resolved').length,
                accent: '#db2777'
            },
            {
                label: 'Employees With Alerts',
                value: employees.filter((item) => item.escalationTriggered).length,
                accent: '#dc2626'
            },
            {
                label: 'Recognitions Logged',
                value: feedbackLogs.filter((item) => item.type === 'recognition').length,
                accent: '#16a34a'
            }
        ],
        [employees, feedbackLogs, requests]
    );

    const resetEntryForm = () => {
        setEntryForm({
            category: '',
            subcategory: '',
            syncroModule: null,
            description: '',
            location: '',
            machineId: '',
            photoUrls: [],
            priority: 'medium',
            assignedTo: '',
            feedbackType: 'complaint',
            feedbackCategory: '',
            targetEmployee: '',
            severity: 'medium',
            isConfidentialSubmission: false
        });
        setEntryMode('request');
    };

    const openEntryDialog = (mode = 'request') => {
        resetEntryForm();
        setEntryMode(mode);
        setShowEntryDialog(true);
    };

    const closeEntryDialog = () => {
        resetEntryForm();
        setShowEntryDialog(false);
    };

    const openTicketDetail = (type, row) => {
        setDetailDialog({ visible: true, type, data: row });
        setDetailComment('');
    };

    const closeTicketDetail = () => {
        setDetailDialog({ visible: false, type: 'request', data: null });
        setDetailComment('');
    };

    const onFieldChange = (field, value) => {
        setEntryForm((prev) => {
            const next = { ...prev, [field]: value };
            if (field === 'category') {
                next.subcategory = '';
                next.syncroModule = null;
                next.assignedTo = '';
                if (value === SYNCRO_REQUEST_CATEGORY) {
                    next.machineId = '';
                    next.location = '';
                }
            }
            if (field === 'subcategory') {
                next.assignedTo = '';
            }
            if (field === 'feedbackType') {
                next.feedbackCategory = '';
                next.severity = value === 'recognition' ? '' : 'medium';
            }
            if (field === 'machineId') {
                const machine = machines.find((item) => item._id === value);
                if (machine?.location && !next.location) {
                    next.location = machine.location;
                }
            }
            return next;
        });
    };

    const addEntryPhoto = (photoValue) => {
        if (!photoValue) return;
        setEntryForm((prev) => {
            if ((prev.photoUrls || []).length >= MAX_HR_PHOTOS) {
                toast.warn(t(`Maximum ${MAX_HR_PHOTOS} photos allowed.`));
                return prev;
            }

            return {
                ...prev,
                photoUrls: [...(prev.photoUrls || []), photoValue]
            };
        });
    };

    const removeEntryPhoto = (index) => {
        setEntryForm((prev) => ({
            ...prev,
            photoUrls: (prev.photoUrls || []).filter((_, currentIndex) => currentIndex !== index)
        }));
    };

    const submitEntry = async () => {
        if (submitting) return;
        setSubmitting(true);

        let response = null;
        if (entryMode === 'request') {
            response = await dispatch(
                handlePostRequest(
                    {
                        category: entryForm.category,
                        subcategory: entryForm.subcategory,
                        syncroModule: isSyncroRequest ? entryForm.syncroModule || null : null,
                        description: entryForm.description,
                        location: entryForm.location,
                        machineId: entryForm.machineId || null,
                        priority: entryForm.priority,
                        assignedTo: entryForm.assignedTo || null,
                        photoUrls: entryForm.photoUrls || []
                    },
                    endPoints.hrRequests,
                    true
                )
            );
        } else {
            response = await dispatch(
                handlePostRequest(
                    {
                        type: entryForm.feedbackType,
                        targetEmployee: entryForm.targetEmployee,
                        category: entryForm.feedbackCategory,
                        description: entryForm.description,
                        location: entryForm.location,
                        severity: entryForm.feedbackType === 'complaint' ? entryForm.severity : null,
                        isConfidentialSubmission: Boolean(entryForm.isConfidentialSubmission),
                        photoUrls: entryForm.photoUrls || []
                    },
                    endPoints.hrFeedback,
                    true
                )
            );
        }

        setSubmitting(false);

        if (response?.status === 200 || response?.status === 201) {
            closeEntryDialog();
            loadModuleData();
        }
    };

    const syncDetailRecord = (type, nextRecord) => {
        if (!nextRecord?._id) return;
        setDetailDialog((prev) => {
            if (!prev.visible || prev.type !== type || prev.data?._id !== nextRecord._id) {
                return prev;
            }
            return { ...prev, data: nextRecord };
        });
    };

    const updateDetailModule = async (value) => {
        const row = detailDialog.data;
        if (!row?._id || savingModule) return;
        setSavingModule(true);
        try {
            const response = await dispatch(handlePatchRequest({ syncroModule: value || null }, endPoints.updateHrRequest(row._id), true));
            if (response?.status === 200) {
                syncDetailRecord('request', response?.data?.data);
                await loadModuleData();
            }
        } finally {
            setSavingModule(false);
        }
    };

    const renderEntryModule = () => isSyncroRequest ? (
        <div className="col-12 md:col-6">
            <label htmlFor="hr-entry-module" className="font-medium mb-2 block">{t('Syncro module (optional)')}</label>
            <Dropdown inputId="hr-entry-module" value={entryForm.syncroModule} options={syncroModuleOptions}
                onChange={(e) => onFieldChange('syncroModule', e.value)} placeholder={t('Select a sidebar module')}
                filter showClear className="w-full" />
        </div>
    ) : null;

    const renderDetailModule = (canEdit) => detailDialog.type === 'request' && detailDialog.data?.category === SYNCRO_REQUEST_CATEGORY ? (
        <div className="mb-4">
            <label htmlFor="hr-detail-module" className="font-medium mb-2 block">{t('Syncro module (optional)')}</label>
            {canEdit ? (
                <Dropdown inputId="hr-detail-module" value={detailDialog.data.syncroModule || null}
                    options={detailDialog.data.syncroModule && !syncroModuleOptions.some((option) => option.value === detailDialog.data.syncroModule)
                        ? [...syncroModuleOptions, { label: detailDialog.data.syncroModule, value: detailDialog.data.syncroModule }]
                        : syncroModuleOptions}
                    onChange={(e) => updateDetailModule(e.value)} placeholder={t('Select a sidebar module')}
                    filter showClear disabled={savingModule} className="w-full" />
            ) : (
                <div>{syncroModuleOptions.find((option) => option.value === detailDialog.data.syncroModule)?.label || detailDialog.data.syncroModule || t('No module selected')}</div>
            )}
        </div>
    ) : null;

    const updateFeedbackField = async (row, field, value) => {
        const response = await dispatch(handlePatchRequest({ [field]: value }, endPoints.updateHrFeedback(row._id), true));
        if (response?.status === 200) {
            syncDetailRecord('feedback', response?.data?.data);
            loadModuleData();
            if (employeeProfile?.employee?._id === row?.targetEmployee?._id) {
                fetchEmployeeProfile(row.targetEmployee._id);
            }
        }
    };

    const updateRequestAssignee = async (row, assignedTo) => {
        const response = await dispatch(
            handlePatchRequest(
                { assignedTo: assignedTo || null },
                endPoints.updateHrRequest(row._id),
                true
            )
        );
        if (response?.status === 200) {
            syncDetailRecord('request', response?.data?.data);
            loadModuleData();
        }
    };

    const addDetailComment = async () => {
        const comment = String(detailComment || '').trim();
        if (!comment || !detailDialog?.data?._id) return;

        const endPoint =
            detailDialog.type === 'request'
                ? endPoints.updateHrRequest(detailDialog.data._id)
                : endPoints.updateHrFeedback(detailDialog.data._id);

        const response = await dispatch(handlePatchRequest({ comment }, endPoint, true));
        if (response?.status === 200) {
            syncDetailRecord(detailDialog.type, response?.data?.data);
            setDetailComment('');
            loadModuleData();
        }
    };

    const fetchEmployeeProfile = async (employeeId) => {
        setLoadingProfileId(employeeId);
        const response = await handleGetRequest(endPoints.hrEmployeeProfile(employeeId), true, false, dispatch);
        setLoadingProfileId('');
        if (response?.data) {
            setEmployeeProfile(response.data);
            setProfileDialog(true);
        }
    };

    const routeOwnersText = (owners) => {
        const names = (owners || []).map((owner) => formatUserName(owner)).filter(Boolean);
        return names.length ? names.join(', ') : '-';
    };

    const routeOwnersTemplate = (rowData) => routeOwnersText(rowData?.routeOwners);
    const statusTagTemplate = (value) => <Tag value={translateHrToken(value, preferredLanguage)} severity={statusSeverity(value)} />;
    const priorityTagTemplate = (value) => <Tag value={translateHrToken(value, preferredLanguage)} severity={statusSeverity(value)} />;
    const requestSummaryTemplate = (rowData) => (
        <div>
            <div className="font-medium text-900">{t(rowData?.subcategory || '-')}</div>
            <div className="text-600 text-sm">{t(rowData?.category || '-')}</div>
        </div>
    );
    const feedbackSummaryTemplate = (rowData) => (
        <div>
            <div className="font-medium text-900">{t(rowData?.category || '-')}</div>
            <div className="text-600 text-sm">{t(rowData?.routingLabel || '-')}</div>
        </div>
    );
    const employeeNameTemplate = (rowData) => `${rowData?.firstName || ''} ${rowData?.lastName || ''}`.trim() || rowData?.email || '-';
    const personTemplate = (rowData, field) => `${rowData?.[field]?.firstName || ''} ${rowData?.[field]?.lastName || ''}`.trim() || rowData?.[field]?.email || '-';
    const machineTemplate = (rowData) => rowData?.machine?.uuid || rowData?.machine?.serialNumber || '-';
    const teamTemplate = (rowData) => rowData?.team?.name || rowData?.team?.title || '-';
    const roleTemplate = (rowData) => rowData?.role?.title || rowData?.role?.uid || '-';
    const feedbackLoggedByTemplate = (rowData) => (rowData?.isConfidentialSubmission ? t('Confidential Submission') : personTemplate(rowData, 'createdBy'));
    const elapsedTimeTemplate = (rowData) => {
        const minutes = calculateElapsedMinutes(rowData?.createdAt, rowData?.status === 'resolved' ? rowData?.closedAt : null);
        if (minutes === null) return '-';
        return <span style={{ color: getElapsedColor(minutes), fontWeight: 700 }}>{formatTimeFromMinutes(minutes)}</span>;
    };
    const clearRequestFilters = () => setRequestFilters({ status: null, priority: null, category: null, routingKey: null });
    const clearFeedbackFilters = () => setFeedbackFilters({ type: null, status: null, severity: null, targetEmployee: null, routingKey: null });
    const clearEmployeeFilters = () => setEmployeeFilters({ role: null, team: null, alertsOnly: false });

    const entryDialogDisabled =
        entryMode === 'request'
            ? !entryForm.category || !entryForm.subcategory || !entryForm.description
            : !entryForm.feedbackType || !entryForm.feedbackCategory || !entryForm.targetEmployee || !entryForm.description || (entryForm.feedbackType === 'complaint' && !entryForm.severity);
    const detailComments = useMemo(
        () => [...(detailDialog?.data?.comments || [])].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
        [detailDialog]
    );

    const updateDetailStatus = async (status) => {
        if (!detailDialog?.data?._id) return;

        const endPoint =
            detailDialog.type === 'request'
                ? endPoints.updateHrRequest(detailDialog.data._id)
                : endPoints.updateHrFeedback(detailDialog.data._id);

        const nextStatus = detailDialog.type === 'feedback' && status === 'in_progress' ? 'in_review' : status;
        const response = await dispatch(handlePatchRequest({ status: nextStatus }, endPoint, true));
        if (response?.status === 200) {
            syncDetailRecord(detailDialog.type, response?.data?.data);
            loadModuleData();
        }
    };

    const updateRoutingSettingOwners = async (setting, assignedUsers) => {
        if (!setting?.configKey || savingRoutingKey) return;

        setSavingRoutingKey(setting.configKey);
        const response = await dispatch(
            handlePatchRequest(
                {
                    configKey: setting.configKey,
                    assignedUsers: assignedUsers || []
                },
                endPoints.updateHrRoutingSetting,
                true
            )
        );
        setSavingRoutingKey('');

        if (response?.status === 200) {
            loadModuleData();
        }
    };

    const routingOwnersEditorTemplate = (setting) => (
        <MultiSelect
            value={(setting?.assignedUsers || []).map((item) => item._id)}
            options={assigneeOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => updateRoutingSettingOwners(setting, e.value)}
            placeholder={t('Select route owners')}
            display="chip"
            className="w-full"
            maxSelectedLabels={2}
            filter
            disabled={savingRoutingKey === setting.configKey}
        />
    );

    const renderRequestAssignee = () => {
        if (!isAdmin || !entryForm.category || !entryForm.subcategory) return null;

        return (
            <div className="col-12 md:col-6">
                <label htmlFor="hr-entry-assignee" className="font-medium mb-2 block">{t('Assigned To')}</label>
                <Dropdown
                    inputId="hr-entry-assignee"
                    value={entryForm.assignedTo || null}
                    options={selectedRequestAssigneeOptions}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => onFieldChange('assignedTo', e.value)}
                    placeholder={t('Select responsible')}
                    emptyMessage={t('No route owners configured')}
                    className="w-full"
                    showClear
                />
            </div>
        );
    };

    // ────────── Non-admin portal ──────────
    const currentUserId = currentAdmin?._id;

    const myRequests = useMemo(
        () => requests.filter((item) => String(item?.createdBy?._id || item?.createdBy) === String(currentUserId)),
        [requests, currentUserId]
    );

    const routedRequests = useMemo(
        () =>
            requests.filter(
                (item) =>
                    String(item?.createdBy?._id || item?.createdBy) !== String(currentUserId) &&
                    (
                        (item?.routeOwners || []).some((owner) => String(owner?._id || owner) === String(currentUserId)) ||
                        String(item?.assignedTo?._id || item?.assignedTo || '') === String(currentUserId)
                    )
            ),
        [requests, currentUserId]
    );

    const myFeedback = useMemo(
        () => feedbackLogs.filter((item) => String(item?.createdBy?._id || item?.createdBy) === String(currentUserId)),
        [feedbackLogs, currentUserId]
    );

    const routedFeedback = useMemo(
        () =>
            feedbackLogs.filter(
                (item) =>
                    String(item?.createdBy?._id || item?.createdBy) !== String(currentUserId) &&
                    (
                        (item?.routeOwners || []).some((owner) => String(owner?._id || owner) === String(currentUserId)) ||
                        String(item?.assignedTo?._id || item?.assignedTo || '') === String(currentUserId)
                    )
            ),
        [feedbackLogs, currentUserId]
    );

    const hasRoutedItems = routedRequests.length > 0 || routedFeedback.length > 0;

    const portalReassignOptions = useMemo(
        () =>
            employees
                .filter((item) => item?.role?.uid !== 'operator')
                .map((item) => ({
                    label: `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email,
                    value: item._id
                })),
        [employees]
    );

    const handleReassignRouteOwners = async (ticket, type, newOwnerIds) => {
        const endPoint = type === 'request' ? endPoints.updateHrRequest(ticket._id) : endPoints.updateHrFeedback(ticket._id);
        const response = await dispatch(handlePatchRequest({ routeOwners: newOwnerIds }, endPoint, true));
        if (response?.status === 200) {
            syncDetailRecord(type, response?.data?.data);
            loadModuleData();
        }
    };

    if (!isAdmin) {
        const portalMyItems = [...myRequests.map((r) => ({ ...r, _itemType: 'request' })), ...myFeedback.map((f) => ({ ...f, _itemType: 'feedback' }))]
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        const portalRoutedItems = [...routedRequests.map((r) => ({ ...r, _itemType: 'request' })), ...routedFeedback.map((f) => ({ ...f, _itemType: 'feedback' }))]
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

        const portalTabs = [
            { key: 'mine', label: t('My Requests') },
            ...(hasRoutedItems ? [{ key: 'routed', label: t('Assigned to Me') }] : [])
        ];

        // Summary cards for route owners
        const now = new Date();
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);

        const routedOpen = portalRoutedItems.filter((i) => i.status !== 'resolved').length;
        const routedInProgress = portalRoutedItems.filter((i) => i.status === 'in_progress' || i.status === 'in_review').length;
        const routedResolvedThisWeek = portalRoutedItems.filter(
            (i) => i.status === 'resolved' && i.closedAt && new Date(i.closedAt) >= weekAgo
        ).length;

        const portalSummaryCards = hasRoutedItems
            ? [
                  { label: t('Open Assigned'), value: routedOpen, accent: '#2563eb' },
                  { label: t('In Progress'), value: routedInProgress, accent: '#eab308' },
                  { label: t('Resolved This Week'), value: routedResolvedThisWeek, accent: '#16a34a' }
              ]
            : [];

        const routedStatusOptions = [
            { label: t('Open'), value: 'open' },
            { label: t('In Progress'), value: 'in_progress' },
            { label: t('Resolved'), value: 'resolved' }
        ];
        const routedSortOptions = [
            { label: t('Newest First'), value: 'newest' },
            { label: t('Oldest First'), value: 'oldest' }
        ];

        const filteredRoutedItems = portalRoutedItems
            .filter((item) => {
                if (!routedStatusFilter) return true;
                if (routedStatusFilter === 'in_progress') return item.status === 'in_progress' || item.status === 'in_review';
                return item.status === routedStatusFilter;
            })
            .sort((a, b) => {
                if (routedSortOrder === 'oldest') return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
                return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
            });

        // Urgency border color based on elapsed time
        const getUrgencyBorderColor = (item) => {
            if (item.status === 'resolved') return '#22c55e';
            const minutes = calculateElapsedMinutes(item.createdAt, null);
            return getElapsedColor(minutes);
        };

        const portalItemTemplate = (item, showSubmitter = false) => {
            const isRequest = item._itemType === 'request';
            const title = isRequest ? (item.subcategory || item.category || '-') : (item.category || '-');
            const subtitle = isRequest ? (item.category || '') : (item.type === 'complaint' ? t('Complaint') : t('Recognition'));
            const submitterName = item.createdBy
                ? `${item.createdBy.firstName || ''} ${item.createdBy.lastName || ''}`.trim() || item.createdBy.email || ''
                : item.isConfidentialSubmission ? t('Confidential Submission') : '';
            const coOwnerCount = (item.routeOwners || []).length;
            const urgencyColor = getUrgencyBorderColor(item);

            return (
                <div
                    className="surface-card border-round-lg p-3 mb-2 cursor-pointer hover:surface-hover"
                    style={{
                        border: '1px solid var(--surface-border)',
                        borderLeft: `4px solid ${urgencyColor}`,
                        transition: 'background-color 0.15s'
                    }}
                    onClick={() => openTicketDetail(item._itemType, item)}
                >
                    <div className="flex justify-content-between align-items-start gap-2 flex-wrap">
                        <div className="flex-1">
                            <div className="flex align-items-center gap-2 mb-1 flex-wrap">
                                <Tag
                                    value={isRequest ? t('Request') : t('Feedback')}
                                    severity={isRequest ? 'info' : 'warning'}
                                    style={{ fontSize: '0.7rem' }}
                                />
                                {statusTagTemplate(item.status)}
                                {isRequest && item.priority ? priorityTagTemplate(item.priority) : null}
                            </div>
                            <div className="text-900 font-medium">{t(title)}</div>
                            <div className="text-600 text-sm">{t(subtitle)}</div>
                            {showSubmitter && submitterName ? (
                                <div className="text-500 text-sm mt-1">
                                    <i className="pi pi-user mr-1" style={{ fontSize: '0.75rem' }} />
                                    {submitterName}
                                </div>
                            ) : null}
                        </div>
                        <div className="text-right" style={{ minWidth: '10rem' }}>
                            <div className="text-600 text-sm">{formatDateTime(item.createdAt)}</div>
                            {item.status !== 'resolved' ? <div className="mt-1">{elapsedTimeTemplate(item)}</div> : null}
                            {showSubmitter && item.routingLabel ? (
                                <div className="text-500 text-sm mt-1">{t(item.routingLabel)}</div>
                            ) : null}
                            {showSubmitter && coOwnerCount > 1 ? (
                                <div className="text-500 text-sm mt-1">
                                    <i className="pi pi-users mr-1" style={{ fontSize: '0.7rem' }} />
                                    {coOwnerCount} {t('owners')}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    {item.description ? (
                        <div className="text-700 text-sm mt-2" style={{ maxHeight: '2.6rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.description}
                        </div>
                    ) : null}
                </div>
            );
        };

        return (
            <div className="grid">
                <div className="col-12">
                    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-start gap-3 mb-4">
                        <div>
                            <h1 className="m-0 mb-2" style={{ fontSize: '2rem' }}>{t('Operations & People')}</h1>
                            <p className="m-0 line-height-3 text-600" style={{ maxWidth: '62rem' }}>
                                {t('Submit operational requests or feedback, and track your tickets.')}
                            </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <Button label={t('New Request')} icon="pi pi-plus" className="p-button-sm" onClick={() => openEntryDialog('request')} />
                        </div>
                    </div>
                </div>

                {portalSummaryCards.map((card) => (
                    <div className="col-12 md:col-4" key={card.label}>
                        <div className="surface-card border-round-xl p-4 h-full" style={{ borderTop: `4px solid ${card.accent}` }}>
                            <div className="text-600 text-sm mb-2">{card.label}</div>
                            <div className="text-900" style={{ fontSize: '2rem', fontWeight: 700 }}>{card.value}</div>
                        </div>
                    </div>
                ))}

                <div className="col-12">
                    <div className="surface-card border-round-xl p-4">
                        <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                            <TabPanel header={portalTabs[0]?.label}>
                                {portalMyItems.length === 0 ? (
                                    <div className="surface-ground border-round-lg p-5 text-center text-600">
                                        {t('You have not submitted any requests yet.')}
                                    </div>
                                ) : (
                                    portalMyItems.map((item) => <div key={item._id}>{portalItemTemplate(item, false)}</div>)
                                )}
                            </TabPanel>
                            {hasRoutedItems && (
                                <TabPanel header={portalTabs[1]?.label}>
                                    <div className="flex gap-2 mb-3 flex-wrap align-items-center">
                                        <Dropdown
                                            value={routedStatusFilter}
                                            options={routedStatusOptions}
                                            optionLabel="label"
                                            optionValue="value"
                                            onChange={(e) => setRoutedStatusFilter(e.value)}
                                            placeholder={t('All Statuses')}
                                            className="w-auto"
                                            showClear
                                            style={{ minWidth: '12rem' }}
                                        />
                                        <Dropdown
                                            value={routedSortOrder}
                                            options={routedSortOptions}
                                            optionLabel="label"
                                            optionValue="value"
                                            onChange={(e) => setRoutedSortOrder(e.value)}
                                            className="w-auto"
                                            style={{ minWidth: '12rem' }}
                                        />
                                    </div>
                                    {filteredRoutedItems.length === 0 ? (
                                        <div className="surface-ground border-round-lg p-5 text-center text-600">
                                            {t('No tickets assigned to you.')}
                                        </div>
                                    ) : (
                                        filteredRoutedItems.map((item) => <div key={item._id}>{portalItemTemplate(item, true)}</div>)
                                    )}
                                </TabPanel>
                            )}
                        </TabView>
                    </div>
                </div>

                {/* Entry dialog — shared with admin */}
                <Dialog
                    header={t('New Request')}
                    visible={showEntryDialog}
                    onHide={closeEntryDialog}
                    style={{ width: 'min(92vw, 62rem)' }}
                >
                    <div className="grid">
                        <div className="col-12">
                            <label className="font-medium mb-2 block">{t('What would you like to do?')}</label>
                            <div className="flex gap-2 flex-wrap">
                                {(moduleConfig?.entryOptions || []).map((item) => (
                                    <Button
                                        key={item.key}
                                        label={t(item.label)}
                                        className={entryMode === item.key ? 'p-button-sm' : 'p-button-sm p-button-outlined'}
                                        onClick={() => setEntryMode(item.key)}
                                    />
                                ))}
                            </div>
                        </div>

                        {entryMode === 'request' ? (
                            <>
                                <div className="col-12 md:col-6">
                                    <label className="font-medium mb-2 block">{t('Category')}</label>
                                    <Dropdown value={entryForm.category} options={requestCategoryOptions} onChange={(e) => onFieldChange('category', e.value)} placeholder={t('Select category')} className="w-full" />
                                </div>
                                <div className="col-12 md:col-6">
                                    <label className="font-medium mb-2 block">{t('Subcategory')}</label>
                                    <Dropdown value={entryForm.subcategory} options={requestSubcategoryOptions} onChange={(e) => onFieldChange('subcategory', e.value)} placeholder={t('Select subcategory')} className="w-full" disabled={!entryForm.category} />
                                </div>
                                <div className="col-12 md:col-6">
                                    <label className="font-medium mb-2 block">{t('Priority')}</label>
                                    <Dropdown value={entryForm.priority} options={localizedRequestPriorityOptions} optionLabel="label" optionValue="value" onChange={(e) => onFieldChange('priority', e.value)} className="w-full" />
                                </div>
                                {renderEntryModule()}
                                {renderRequestAssignee()}
                                {!isSyncroRequest && (
                                    <>
                                        <div className="col-12 md:col-6">
                                            <label className="font-medium mb-2 block">{t('Machine')}</label>
                                            <Dropdown value={entryForm.machineId} options={machineOptions} onChange={(e) => onFieldChange('machineId', e.value)} placeholder={t('Optional machine')} filter className="w-full" showClear />
                                        </div>
                                        <div className="col-12 md:col-6">
                                            <label className="font-medium mb-2 block">{t('Location')}</label>
                                            <InputText value={entryForm.location} onChange={(e) => onFieldChange('location', e.target.value)} placeholder={t('Optional location')} className="w-full" />
                                        </div>
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="col-12 md:col-6">
                                    <label className="font-medium mb-2 block">{t('Feedback Type')}</label>
                                    <Dropdown value={entryForm.feedbackType} options={localizedFeedbackTypeOptions} optionLabel="label" optionValue="value" onChange={(e) => onFieldChange('feedbackType', e.value)} className="w-full" />
                                </div>
                                <div className="col-12 md:col-6">
                                    <label className="font-medium mb-2 block">{t('Employee')}</label>
                                    <Dropdown value={entryForm.targetEmployee} options={employeeOptions} onChange={(e) => onFieldChange('targetEmployee', e.value)} placeholder={t('Select employee')} filter className="w-full" />
                                </div>
                                <div className="col-12 md:col-6">
                                    <label className="font-medium mb-2 block">{t('Category')}</label>
                                    <Dropdown value={entryForm.feedbackCategory} options={feedbackCategoryOptions} onChange={(e) => onFieldChange('feedbackCategory', e.value)} placeholder={t('Select feedback category')} className="w-full" />
                                </div>
                                <div className="col-12 md:col-6">
                                    <label className="font-medium mb-2 block">{t('Location')}</label>
                                    <InputText value={entryForm.location} onChange={(e) => onFieldChange('location', e.target.value)} placeholder={t('Optional location')} className="w-full" />
                                </div>
                                <div className="col-12">
                                    <div className="flex align-items-center gap-2">
                                        <Checkbox inputId="hrConfidentialSubmissionPortal" checked={Boolean(entryForm.isConfidentialSubmission)} onChange={(e) => onFieldChange('isConfidentialSubmission', e.checked)} />
                                        <label htmlFor="hrConfidentialSubmissionPortal" className="font-medium">{t('Confidential Submission')}</label>
                                    </div>
                                </div>
                                {entryForm.feedbackType === 'complaint' && (
                                    <div className="col-12 md:col-6">
                                        <label className="font-medium mb-2 block">{t('Severity')}</label>
                                        <Dropdown value={entryForm.severity} options={localizedSeverityOptions} optionLabel="label" optionValue="value" onChange={(e) => onFieldChange('severity', e.value)} className="w-full" />
                                    </div>
                                )}
                            </>
                        )}

                        <div className="col-12">
                            <label className="font-medium mb-2 block">{t('Photos')}</label>
                            <PhotoUploader value="" onChange={addEntryPhoto} maxUploadBytes={500 * 1024} maxDimension={1280} helperText={t(`Optional. Up to ${MAX_HR_PHOTOS} photos. Max 500 KB each.`)} />
                            {entryForm.photoUrls.length > 0 && (
                                <div className="mt-3">
                                    <small className="text-500 block mb-2">{entryForm.photoUrls.length} {entryForm.photoUrls.length !== 1 ? t('photos') : t('photo')} {t('added')}</small>
                                    <div className="grid">
                                        {entryForm.photoUrls.map((photo, index) => (
                                            <div className="col-12 md:col-4" key={`hr-entry-photo-portal-${index}`}>
                                                <div style={{ border: '1px solid rgba(148,163,184,0.4)', borderRadius: '6px', padding: '0.5rem' }}>
                                                    <img src={photo} alt={`HR upload ${index + 1}`} style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '4px' }} />
                                                    <div className="flex justify-content-end mt-2">
                                                        <Button type="button" label={t('Remove')} icon="pi pi-times" className="p-button-danger p-button-text p-button-sm" onClick={() => removeEntryPhoto(index)} />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="col-12">
                            <label className="font-medium mb-2 block">{t('Description')}</label>
                            <InputTextarea value={entryForm.description} onChange={(e) => onFieldChange('description', e.target.value)} rows={5} className="w-full" placeholder={t(isSyncroRequest ? 'Describe what happened, what you expected, and the affected Syncro screen or module' : 'Describe the request or feedback')} />
                        </div>
                    </div>

                    <div className="flex justify-content-end gap-2 mt-4">
                        <Button label={t('Cancel')} className="p-button-text" onClick={closeEntryDialog} />
                        <Button label={submitting ? t('Saving...') : t('Save')} onClick={submitEntry} disabled={entryDialogDisabled || submitting} />
                    </div>
                </Dialog>

                {/* Detail dialog for non-admin portal */}
                <Dialog
                    header={
                        detailDialog.type === 'request'
                            ? t(detailDialog?.data?.subcategory || 'Request Detail')
                            : t(detailDialog?.data?.category || 'Feedback Detail')
                    }
                    visible={detailDialog.visible}
                    onHide={closeTicketDetail}
                    style={{ width: 'min(96vw, 70rem)' }}
                >
                    {detailDialog?.data ? (() => {
                        const isOwnerOfTicket = (detailDialog.data?.routeOwners || []).some(
                            (owner) => String(owner?._id || owner) === String(currentUserId)
                        );
                        const isAssignedToTicket =
                            String(detailDialog.data?.assignedTo?._id || detailDialog.data?.assignedTo || '') === String(currentUserId);
                        const isCreatorOfTicket = String(detailDialog.data?.createdBy?._id || detailDialog.data?.createdBy) === String(currentUserId);
                        const canManageTicket = isOwnerOfTicket || isAssignedToTicket;

                        return (
                            <>
                                <div className="grid mb-3">
                                    <div className="col-12 md:col-4">
                                        <div className="surface-ground border-round-lg p-3 h-full">
                                            <div className="text-600 text-sm mb-1">{t('Created')}</div>
                                            <div className="text-900 font-medium">{formatDateTime(detailDialog.data.createdAt)}</div>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <div className="surface-ground border-round-lg p-3 h-full">
                                            <div className="text-600 text-sm mb-1">{t('Status')}</div>
                                            <div className="mt-1">{statusTagTemplate(detailDialog.data.status)}</div>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <div className="surface-ground border-round-lg p-3 h-full">
                                            {detailDialog.type === 'request' ? (
                                                <>
                                                    <div className="text-600 text-sm mb-1">{t('Priority')}</div>
                                                    <div className="mt-1">{priorityTagTemplate(detailDialog.data.priority)}</div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="text-600 text-sm mb-1">{t('Type')}</div>
                                                    <div className="mt-1">
                                                        <Tag value={translateHrToken(detailDialog.data.type, preferredLanguage)} severity={statusSeverity(detailDialog.data.type)} />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid mb-3">
                                    <div className="col-12 md:col-6">
                                        <div className="surface-ground border-round-lg p-3 h-full">
                                            {detailDialog.type === 'request' ? (
                                                <>
                                                    <div className="text-600 text-sm mb-1">{t('Category')}</div>
                                                    <div className="text-900 font-medium">{t(detailDialog.data.category || '-')}</div>
                                                    <div className="text-600 text-sm mt-3 mb-1">{t('Subcategory')}</div>
                                                    <div className="text-900">{t(detailDialog.data.subcategory || '-')}</div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="text-600 text-sm mb-1">{t('Category')}</div>
                                                    <div className="text-900 font-medium">{t(detailDialog.data.category || '-')}</div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="surface-ground border-round-lg p-3 h-full">
                                            <div className="text-600 text-sm mb-1">{t('Location')}</div>
                                            <div className="text-900">{detailDialog.data.location || '-'}</div>
                                            {detailDialog.type === 'request' ? (
                                                <>
                                                    <div className="text-600 text-sm mt-3 mb-1">{t('Machine')}</div>
                                                    <div className="text-900">{machineTemplate(detailDialog.data)}</div>
                                                </>
                                            ) : null}
                                            {detailDialog.data.closedAt ? (
                                                <>
                                                    <div className="text-600 text-sm mt-3 mb-1">{t('Closed At')}</div>
                                                    <div className="text-900">{formatDateTime(detailDialog.data.closedAt)}</div>
                                                </>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>

                                {renderDetailModule(canManageTicket || isCreatorOfTicket)}

                                <div className="mb-4">
                                    <label className="font-medium mb-2 block">{t('Description')}</label>
                                    <div className="surface-ground border-round-lg p-3 text-900" style={{ whiteSpace: 'pre-wrap' }}>
                                        {detailDialog.data.description || '-'}
                                    </div>
                                </div>

                                {Array.isArray(detailDialog.data.photoUrls) && detailDialog.data.photoUrls.length > 0 ? (
                                    <div className="mb-4">
                                        <h4 className="m-0 mb-2">{t('Photos')}</h4>
                                        <div className="grid">
                                            {detailDialog.data.photoUrls.map((photo, index) => (
                                                <div className="col-12 md:col-4" key={`hr-portal-detail-photo-${index}`}>
                                                    <div style={{ border: '1px solid rgba(148,163,184,0.4)', borderRadius: '6px', padding: '0.5rem' }}>
                                                        <img src={photo} alt={`Attachment ${index + 1}`} style={{ width: '100%', maxHeight: '180px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }} onClick={() => window.open(photo, '_blank', 'noopener,noreferrer')} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}

                                {/* Route owner actions: reassign + status changes */}
                                {canManageTicket && (
                                    <>
                                        <div className="mb-4">
                                            <label className="font-medium mb-2 block">{t('Reassign Route Owners')}</label>
                                            <MultiSelect
                                                value={(detailDialog.data.routeOwners || []).map((o) => o._id || o)}
                                                options={portalReassignOptions}
                                                optionLabel="label"
                                                optionValue="value"
                                                onChange={(e) => handleReassignRouteOwners(detailDialog.data, detailDialog.type, e.value)}
                                                placeholder={t('Select route owners')}
                                                display="chip"
                                                className="w-full"
                                                filter
                                                maxSelectedLabels={3}
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Comments */}
                                {(canManageTicket || isCreatorOfTicket) && (
                                    <div className="mb-3">
                                        <div className="flex justify-content-between align-items-center mb-2">
                                            <h4 className="m-0">{t('Comments')}</h4>
                                            <span className="text-600 text-sm">{detailComments.length} {detailComments.length === 1 ? t('comment') : t('comments')}</span>
                                        </div>
                                        {canManageTicket && (
                                            <div className="grid mb-2">
                                                <div className="col-12 md:col-8">
                                                    <InputTextarea value={detailComment} onChange={(e) => setDetailComment(e.target.value)} rows={3} className="w-full" placeholder={t('Add a comment')} />
                                                </div>
                                                <div className="col-12 md:col-4 flex align-items-end">
                                                    <Button label={t('Add Comment')} className="w-full" onClick={addDetailComment} disabled={!String(detailComment || '').trim()} />
                                                </div>
                                            </div>
                                        )}
                                        {detailComments.length > 0 ? (
                                            detailComments.map((item) => (
                                                <div key={item._id || `${item.createdAt}-${item.comment}`} className="surface-ground border-round-lg p-3 mb-2">
                                                    <div className="flex justify-content-between gap-2 flex-wrap mb-2">
                                                        <span className="font-medium text-900">{formatUserName(item.createdBy)}</span>
                                                        <span className="text-600 text-sm">{formatDateTime(item.createdAt)}</span>
                                                    </div>
                                                    <div className="text-900" style={{ whiteSpace: 'pre-wrap' }}>{item.comment}</div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="surface-ground border-round-lg p-3 text-600">{t('No comments yet.')}</div>
                                        )}
                                    </div>
                                )}

                                {/* Status actions for route owners */}
                                {canManageTicket && (
                                    <div className="flex justify-content-end gap-2 flex-wrap">
                                        <Button label={t('Mark Open')} className="p-button-outlined" onClick={() => updateDetailStatus('open')} disabled={detailDialog?.data?.status === 'open'} />
                                        <Button label={t('Mark In Progress')} className="p-button-warning" onClick={() => updateDetailStatus('in_progress')} disabled={detailDialog.type === 'request' ? detailDialog?.data?.status === 'in_progress' : detailDialog?.data?.status === 'in_review'} />
                                        <Button label={t('Close Ticket')} className="p-button-success" onClick={() => updateDetailStatus('resolved')} disabled={detailDialog?.data?.status === 'resolved'} />
                                    </div>
                                )}
                            </>
                        );
                    })() : null}
                </Dialog>
            </div>
        );
    }

    // ────────── Admin view (unchanged) ──────────
    return (
        <div className="grid">
            <div className="col-12">
                <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-start gap-3 mb-4">
                    <div>
                        <h1 className="m-0 mb-2" style={{ fontSize: '2rem' }}>{t('Operations & People')}</h1>
                        <p className="m-0 line-height-3 text-600" style={{ maxWidth: '62rem' }}>
                            {t('Admin-only workspace for operational requests, internal people feedback, employee timelines, and future performance reviews.')}
                        </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <Button label={t('New Request')} icon="pi pi-plus" className="p-button-sm" onClick={() => openEntryDialog('request')} />
                    </div>
                </div>
            </div>

            {summaryCards.map((card) => (
                <div className="col-12 md:col-6 xl:col-3" key={card.label}>
                    <div className="surface-card border-round-xl p-4 h-full" style={{ borderTop: `4px solid ${card.accent}` }}>
                        <div className="text-600 text-sm mb-2">{t(card.label)}</div>
                        <div className="text-900" style={{ fontSize: '2rem', fontWeight: 700 }}>{card.value}</div>
                    </div>
                </div>
            ))}

            <div className="col-12">
                <div className="surface-card border-round-xl p-4">
                    <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                        <TabPanel header={t('Requests')}>
                            <div className="flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
                                <div>
                                    <h3 className="m-0">{t('Operational Requests')}</h3>
                                    <span className="text-600 text-sm">{t('Admin-managed intake for vending operator requests.')}</span>
                                </div>
                                <InputText value={requestSearch} onChange={(e) => setRequestSearch(e.target.value)} placeholder={t('Search requests')} style={{ minWidth: '16rem' }} />
                            </div>
                            <div className="grid mb-3">
                                <div className="col-12 md:col-4 xl:col-2">
                                    <Dropdown value={requestFilters.status} options={localizedRequestStatusOptions} optionLabel="label" optionValue="value" onChange={(e) => setRequestFilters((prev) => ({ ...prev, status: e.value }))} placeholder={t('Status')} className="w-full" showClear />
                                </div>
                                <div className="col-12 md:col-4 xl:col-2">
                                    <Dropdown value={requestFilters.priority} options={localizedRequestPriorityOptions} optionLabel="label" optionValue="value" onChange={(e) => setRequestFilters((prev) => ({ ...prev, priority: e.value }))} placeholder={t('Priority')} className="w-full" showClear />
                                </div>
                                <div className="col-12 md:col-4 xl:col-2">
                                    <Dropdown value={requestFilters.category} options={requestCategoryOptions} onChange={(e) => setRequestFilters((prev) => ({ ...prev, category: e.value }))} placeholder={t('Category')} className="w-full" showClear />
                                </div>
                                <div className="col-12 md:col-4 xl:col-2">
                                    <Dropdown value={requestFilters.routingKey} options={requestRoutingOptions} onChange={(e) => setRequestFilters((prev) => ({ ...prev, routingKey: e.value }))} placeholder={t('Routing')} className="w-full" showClear />
                                </div>
                                <div className="col-12">
                                    <Button label={t('Clear Filters')} className="p-button-text p-button-sm" onClick={clearRequestFilters} />
                                </div>
                            </div>
                            <DataTable value={openRequests} paginator rows={10} rowsPerPageOptions={[10, 25, 50]} emptyMessage={t('No open requests.')}>
                                <Column field="createdAt" header={t('Created')} body={(row) => formatDateTime(row.createdAt)} sortable style={{ minWidth: '11rem' }} />
                                <Column header={t('Time Elapsed')} body={elapsedTimeTemplate} style={{ minWidth: '10rem' }} />
                                <Column header={t('Opened By')} body={(row) => personTemplate(row, 'createdBy')} style={{ minWidth: '12rem' }} />
                                <Column header={t('Request')} body={requestSummaryTemplate} style={{ minWidth: '22rem' }} />
                                <Column header={t('Priority')} body={(row) => priorityTagTemplate(row.priority)} style={{ minWidth: '10rem' }} />
                                <Column header={t('Status')} body={(row) => statusTagTemplate(row.status)} style={{ minWidth: '10rem' }} />
                                <Column field="routingLabel" header={t('Routing')} body={(row) => t(row.routingLabel || '-')} style={{ minWidth: '12rem' }} />
                                <Column header={t('Assigned To')} body={(row) => personTemplate(row, 'assignedTo')} style={{ minWidth: '12rem' }} />
                                <Column
                                    header={t('Open')}
                                    body={(row) => (
                                        <Button label={t('Open')} className="p-button-sm p-button-outlined" onClick={() => openTicketDetail('request', row)} />
                                    )}
                                    style={{ minWidth: '8rem' }}
                                />
                            </DataTable>

                            <div className="mt-5">
                                <div className="flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
                                    <div>
                                        <h4 className="m-0">{t('Closed Requests')}</h4>
                                        <span className="text-600 text-sm">{t('Resolved requests with recorded close times.')}</span>
                                    </div>
                                </div>
                                <DataTable value={closedRequests} paginator rows={10} rowsPerPageOptions={[10, 25, 50]} emptyMessage={t('No closed requests.')}>
                                    <Column field="createdAt" header={t('Created')} body={(row) => formatDateTime(row.createdAt)} sortable style={{ minWidth: '11rem' }} />
                                    <Column field="closedAt" header={t('Closed')} body={(row) => formatDateTime(row.closedAt)} sortable style={{ minWidth: '11rem' }} />
                                    <Column header={t('Time Elapsed')} body={elapsedTimeTemplate} style={{ minWidth: '10rem' }} />
                                    <Column header={t('Opened By')} body={(row) => personTemplate(row, 'createdBy')} style={{ minWidth: '12rem' }} />
                                    <Column header={t('Request')} body={requestSummaryTemplate} style={{ minWidth: '22rem' }} />
                                    <Column header={t('Priority')} body={(row) => priorityTagTemplate(row.priority)} style={{ minWidth: '8rem' }} />
                                    <Column header={t('Assigned To')} body={(row) => personTemplate(row, 'assignedTo')} style={{ minWidth: '12rem' }} />
                                    <Column
                                        header={t('Open')}
                                        body={(row) => (
                                            <Button label={t('Open')} className="p-button-sm p-button-outlined" onClick={() => openTicketDetail('request', row)} />
                                        )}
                                        style={{ minWidth: '7rem' }}
                                    />
                                </DataTable>
                            </div>
                        </TabPanel>

                        <TabPanel header={t('People Feedback')}>
                            <div className="flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
                                <div>
                                    <h3 className="m-0">{t('Confidential Feedback')}</h3>
                                    <span className="text-600 text-sm">{t('Hidden from employees. Visible only to admins.')}</span>
                                </div>
                                <InputText value={feedbackSearch} onChange={(e) => setFeedbackSearch(e.target.value)} placeholder={t('Search feedback')} style={{ minWidth: '16rem' }} />
                            </div>
                            <div className="grid mb-3">
                                <div className="col-12 md:col-4 xl:col-2">
                                    <Dropdown value={feedbackFilters.type} options={localizedFeedbackTypeOptions} optionLabel="label" optionValue="value" onChange={(e) => setFeedbackFilters((prev) => ({ ...prev, type: e.value }))} placeholder={t('Type')} className="w-full" showClear />
                                </div>
                                <div className="col-12 md:col-4 xl:col-2">
                                    <Dropdown value={feedbackFilters.status} options={localizedFeedbackStatusOptions} optionLabel="label" optionValue="value" onChange={(e) => setFeedbackFilters((prev) => ({ ...prev, status: e.value }))} placeholder={t('Status')} className="w-full" showClear />
                                </div>
                                <div className="col-12 md:col-4 xl:col-2">
                                    <Dropdown value={feedbackFilters.severity} options={localizedSeverityOptions} optionLabel="label" optionValue="value" onChange={(e) => setFeedbackFilters((prev) => ({ ...prev, severity: e.value }))} placeholder={t('Severity')} className="w-full" showClear />
                                </div>
                                <div className="col-12 md:col-4 xl:col-2">
                                    <Dropdown value={feedbackFilters.targetEmployee} options={employeeOptions} onChange={(e) => setFeedbackFilters((prev) => ({ ...prev, targetEmployee: e.value }))} placeholder={t('Employee')} className="w-full" filter showClear />
                                </div>
                                <div className="col-12 md:col-4 xl:col-2">
                                    <Dropdown value={feedbackFilters.routingKey} options={feedbackRoutingOptions} onChange={(e) => setFeedbackFilters((prev) => ({ ...prev, routingKey: e.value }))} placeholder={t('Routing')} className="w-full" showClear />
                                </div>
                                <div className="col-12">
                                    <Button label={t('Clear Filters')} className="p-button-text p-button-sm" onClick={clearFeedbackFilters} />
                                </div>
                            </div>
                            <DataTable value={openFeedback} paginator rows={10} rowsPerPageOptions={[10, 25, 50]} emptyMessage={t('No open feedback.')}>
                                <Column field="createdAt" header={t('Created')} body={(row) => formatDateTime(row.createdAt)} sortable style={{ minWidth: '11rem' }} />
                                <Column header={t('Employee')} body={(row) => personTemplate(row, 'targetEmployee')} style={{ minWidth: '12rem' }} />
                                <Column
                                    field="type"
                                    header={t('Type')}
                                    body={(row) => <Tag value={translateHrToken(row.type, preferredLanguage)} severity={statusSeverity(row.type)} />}
                                    style={{ minWidth: '8rem' }}
                                />
                                <Column header={t('Feedback')} body={feedbackSummaryTemplate} style={{ minWidth: '22rem' }} />
                                <Column
                                    field="severity"
                                    header={t('Severity')}
                                    body={(row) => (row.severity ? <Tag value={translateHrToken(row.severity, preferredLanguage)} severity={statusSeverity(row.severity)} /> : '-')}
                                    style={{ minWidth: '9rem' }}
                                />
                                <Column header={t('Status')} body={(row) => statusTagTemplate(row.status)} style={{ minWidth: '10rem' }} />
                                <Column
                                    header={t('Open')}
                                    body={(row) => (
                                        <Button label={t('Open')} className="p-button-sm p-button-outlined" onClick={() => openTicketDetail('feedback', row)} />
                                    )}
                                    style={{ minWidth: '8rem' }}
                                />
                            </DataTable>

                            <div className="mt-5">
                                <div className="flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
                                    <div>
                                        <h4 className="m-0">{t('Closed Feedback')}</h4>
                                        <span className="text-600 text-sm">{t('Resolved confidential feedback with recorded close times.')}</span>
                                    </div>
                                </div>
                                <DataTable value={closedFeedback} paginator rows={10} rowsPerPageOptions={[10, 25, 50]} emptyMessage={t('No closed feedback.')}>
                                    <Column field="createdAt" header={t('Created')} body={(row) => formatDateTime(row.createdAt)} sortable style={{ minWidth: '11rem' }} />
                                    <Column field="closedAt" header={t('Closed')} body={(row) => formatDateTime(row.closedAt)} sortable style={{ minWidth: '11rem' }} />
                                    <Column header={t('Employee')} body={(row) => personTemplate(row, 'targetEmployee')} style={{ minWidth: '12rem' }} />
                                    <Column
                                        field="type"
                                        header={t('Type')}
                                        body={(row) => <Tag value={translateHrToken(row.type, preferredLanguage)} severity={statusSeverity(row.type)} />}
                                        style={{ minWidth: '8rem' }}
                                    />
                                    <Column header={t('Feedback')} body={feedbackSummaryTemplate} style={{ minWidth: '22rem' }} />
                                    <Column
                                        field="severity"
                                        header={t('Severity')}
                                        body={(row) => (row.severity ? <Tag value={translateHrToken(row.severity, preferredLanguage)} severity={statusSeverity(row.severity)} /> : '-')}
                                        style={{ minWidth: '9rem' }}
                                    />
                                    <Column header={t('Status')} body={(row) => statusTagTemplate(row.status)} style={{ minWidth: '10rem' }} />
                                    <Column
                                        header={t('Open')}
                                        body={(row) => (
                                            <Button label={t('Open')} className="p-button-sm p-button-outlined" onClick={() => openTicketDetail('feedback', row)} />
                                        )}
                                        style={{ minWidth: '8rem' }}
                                    />
                                </DataTable>
                            </div>
                        </TabPanel>

                        <TabPanel header={t('Employees')}>
                            <div className="flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
                                <div>
                                    <h3 className="m-0">{t('Employee Profiles')}</h3>
                                    <span className="text-600 text-sm">{t('Internal scorecards, escalation alerts, and full timeline per employee.')}</span>
                                </div>
                                <InputText value={employeeSearch} onChange={(e) => setEmployeeSearch(e.target.value)} placeholder={t('Search employees')} style={{ minWidth: '16rem' }} />
                            </div>
                            <div className="grid mb-3">
                                <div className="col-12 md:col-4">
                                    <Dropdown value={employeeFilters.role} options={employeeRoleOptions} onChange={(e) => setEmployeeFilters((prev) => ({ ...prev, role: e.value }))} placeholder={t('Role')} className="w-full" showClear />
                                </div>
                                <div className="col-12 md:col-4">
                                    <Dropdown value={employeeFilters.team} options={employeeTeamOptions} onChange={(e) => setEmployeeFilters((prev) => ({ ...prev, team: e.value }))} placeholder={t('Team')} className="w-full" showClear />
                                </div>
                                <div className="col-12 md:col-4 flex align-items-center">
                                    <Button
                                        label={employeeFilters.alertsOnly ? t('Showing Alerts Only') : t('Show Escalations Only')}
                                        className={employeeFilters.alertsOnly ? 'p-button-danger p-button-sm' : 'p-button-outlined p-button-sm'}
                                        onClick={() => setEmployeeFilters((prev) => ({ ...prev, alertsOnly: !prev.alertsOnly }))}
                                    />
                                    <Button label={t('Clear Filters')} className="p-button-text p-button-sm ml-2" onClick={clearEmployeeFilters} />
                                </div>
                            </div>
                            <DataTable value={filteredEmployees} paginator rows={10} rowsPerPageOptions={[10, 25, 50]} emptyMessage={t('No employee data available.')}>
                                <Column header={t('Employee')} body={employeeNameTemplate} sortable />
                                <Column field="email" header={t('Email')} />
                                <Column header={t('Role')} body={roleTemplate} />
                                <Column header={t('Team')} body={teamTemplate} />
                                <Column field="score" header={t('Score')} sortable />
                                <Column field="recognitions" header={t('Recognitions')} sortable />
                                <Column field="complaints" header={t('Complaints')} sortable />
                                <Column field="openRequests" header={t('Open Requests')} sortable />
                                <Column field="assignedRequests" header={t('Assigned Requests')} sortable />
                                <Column
                                    header={t('Alerts')}
                                    body={(row) =>
                                        row.escalationTriggered ? <Tag value={t('Escalated')} severity="danger" /> : <Tag value={t('Normal')} severity="success" />
                                    }
                                />
                                <Column
                                    header={t('Actions')}
                                    body={(row) => (
                                        <Button
                                            label={loadingProfileId === row._id ? t('Loading...') : t('View Profile')}
                                            className="p-button-sm"
                                            onClick={() => fetchEmployeeProfile(row._id)}
                                            disabled={loadingProfileId === row._id}
                                        />
                                    )}
                                />
                            </DataTable>
                        </TabPanel>

                        <TabPanel header={t('Performance Review')}>
                            <div className="surface-ground border-round-xl p-5 text-center">
                                <i className="pi pi-chart-line text-5xl text-500 mb-3" />
                                <h3 className="mt-0 mb-2">{t('Coming Soon')}</h3>
                                <p className="m-0 text-600 line-height-3" style={{ maxWidth: '42rem', marginInline: 'auto' }}>
                                    {t('This section is reserved for future performance reviews that combine managerial evaluation with quantitative signals from operational reports.')}
                                </p>
                            </div>
                        </TabPanel>

                        <TabPanel header={t('Settings')}>
                            <div className="mb-4">
                                <h3 className="m-0">{t('HR Routing Settings')}</h3>
                                <span className="text-600 text-sm">
                                    {t('Configure one or more route owners per category and subcategory. New tickets inherit these owners automatically.')}
                                </span>
                            </div>

                            <div className="mb-5">
                                <div className="flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
                                    <div>
                                        <h4 className="m-0">{t('Request Routing')}</h4>
                                        <span className="text-600 text-sm">{t('Subcategory-level routing for operational requests.')}</span>
                                    </div>
                                </div>
                                <div className="grid mb-3">
                                    <div className="col-12 md:col-4">
                                        <Dropdown
                                            value={requestRoutingCategoryFilter}
                                            options={requestCategoryOptions}
                                            onChange={(e) => setRequestRoutingCategoryFilter(e.value)}
                                            placeholder={t('All Categories')}
                                            className="w-full"
                                            showClear
                                        />
                                    </div>
                                    <div className="col-12 md:col-5">
                                        <span className="p-input-icon-left w-full">
                                            <i className="pi pi-search" />
                                            <InputText
                                                value={requestRoutingSearch}
                                                onChange={(e) => setRequestRoutingSearch(e.target.value)}
                                                placeholder={t('Search routing settings')}
                                                className="w-full"
                                            />
                                        </span>
                                    </div>
                                    <div className="col-12 md:col-3 flex align-items-center">
                                        <Button
                                            label={t('Clear Filters')}
                                            className="p-button-text p-button-sm"
                                            onClick={() => {
                                                setRequestRoutingCategoryFilter(null);
                                                setRequestRoutingSearch('');
                                            }}
                                            disabled={!requestRoutingCategoryFilter && !requestRoutingSearch}
                                        />
                                    </div>
                                </div>
                                <DataTable key={`request-routing-${requestRoutingCategoryFilter || 'all'}-${requestRoutingSearch}`} value={filteredRequestRoutingSettings} paginator rows={10} rowsPerPageOptions={[10, 25, 50]} emptyMessage={t('No request routing settings found.')}>
                                    <Column field="category" header={t('Category')} body={(row) => t(row.category)} style={{ minWidth: '16rem' }} />
                                    <Column field="subcategory" header={t('Subcategory')} body={(row) => t(row.subcategory)} style={{ minWidth: '18rem' }} />
                                    <Column field="routingLabel" header={t('Routing')} body={(row) => t(row.routingLabel)} style={{ minWidth: '14rem' }} />
                                    <Column header={t('Route Owners')} body={routingOwnersEditorTemplate} style={{ minWidth: '18rem' }} />
                                    <Column header={t('Updated By')} body={(row) => formatUserName(row?.updatedBy)} />
                                    <Column field="updatedAt" header={t('Updated')} body={(row) => formatDateTime(row.updatedAt)} />
                                </DataTable>
                            </div>

                            <div>
                                <div className="flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
                                    <div>
                                        <h4 className="m-0">{t('People & HR Routing')}</h4>
                                        <span className="text-600 text-sm">{t('Feedback categories inherit route owners by feedback type and category.')}</span>
                                    </div>
                                </div>
                                <DataTable value={routingSettings.feedback} paginator rows={10} rowsPerPageOptions={[10, 25, 50]} emptyMessage={t('No feedback routing settings found.')}>
                                    <Column field="feedbackType" header={t('Feedback Type')} body={(row) => <Tag value={translateHrToken(row.feedbackType, preferredLanguage)} severity={statusSeverity(row.feedbackType)} />} />
                                    <Column field="category" header={t('Category')} body={(row) => t(row.category)} style={{ minWidth: '18rem' }} />
                                    <Column field="routingLabel" header={t('Routing')} body={(row) => t(row.routingLabel)} style={{ minWidth: '14rem' }} />
                                    <Column header={t('Route Owners')} body={routingOwnersEditorTemplate} style={{ minWidth: '18rem' }} />
                                    <Column header={t('Updated By')} body={(row) => formatUserName(row?.updatedBy)} />
                                    <Column field="updatedAt" header={t('Updated')} body={(row) => formatDateTime(row.updatedAt)} />
                                </DataTable>
                            </div>
                        </TabPanel>
                    </TabView>
                </div>
            </div>

            <Dialog
                header={t('New Request')}
                visible={showEntryDialog}
                onHide={closeEntryDialog}
                style={{ width: 'min(92vw, 62rem)' }}
            >
                <div className="grid">
                    <div className="col-12">
                        <label className="font-medium mb-2 block">{t('What would you like to do?')}</label>
                        <div className="flex gap-2 flex-wrap">
                            {(moduleConfig?.entryOptions || []).map((item) => (
                                <Button
                                    key={item.key}
                                    label={t(item.label)}
                                    className={entryMode === item.key ? 'p-button-sm' : 'p-button-sm p-button-outlined'}
                                    onClick={() => setEntryMode(item.key)}
                                />
                            ))}
                        </div>
                    </div>

                    {entryMode === 'request' ? (
                        <>
                            <div className="col-12 md:col-6">
                                <label className="font-medium mb-2 block">{t('Category')}</label>
                                <Dropdown
                                    value={entryForm.category}
                                    options={requestCategoryOptions}
                                    onChange={(e) => onFieldChange('category', e.value)}
                                    placeholder={t('Select category')}
                                    className="w-full"
                                />
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="font-medium mb-2 block">{t('Subcategory')}</label>
                                <Dropdown
                                    value={entryForm.subcategory}
                                    options={requestSubcategoryOptions}
                                    onChange={(e) => onFieldChange('subcategory', e.value)}
                                    placeholder={t('Select subcategory')}
                                    className="w-full"
                                    disabled={!entryForm.category}
                                />
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="font-medium mb-2 block">{t('Priority')}</label>
                                <Dropdown value={entryForm.priority} options={localizedRequestPriorityOptions} optionLabel="label" optionValue="value" onChange={(e) => onFieldChange('priority', e.value)} className="w-full" />
                            </div>
                            {renderEntryModule()}
                            {renderRequestAssignee()}
                            {!isSyncroRequest && (
                                <>
                                    <div className="col-12 md:col-6">
                                        <label className="font-medium mb-2 block">{t('Machine')}</label>
                                        <Dropdown
                                            value={entryForm.machineId}
                                            options={machineOptions}
                                            onChange={(e) => onFieldChange('machineId', e.value)}
                                            placeholder={t('Optional machine')}
                                            filter
                                            className="w-full"
                                            showClear
                                        />
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <label className="font-medium mb-2 block">{t('Location')}</label>
                                        <InputText value={entryForm.location} onChange={(e) => onFieldChange('location', e.target.value)} placeholder={t('Optional location')} className="w-full" />
                                    </div>
                                </>
                            )}
                            <div className="col-12">
                                <div className="surface-ground border-round-lg px-3 py-2">
                                    <span className="text-600 text-sm">{t('Routing Preview: ')}</span>
                                    <span className="text-900 font-medium">{selectedRequestRouting ? t(selectedRequestRouting) : t('Select category and subcategory')}</span>
                                    {selectedRequestRoutingSetting?.assignedUsers?.length ? (
                                        <div className="text-700 text-sm mt-1">{t('Route Owners')}: {routeOwnersText(selectedRequestRoutingSetting.assignedUsers)}</div>
                                    ) : null}
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="col-12 md:col-6">
                                <label className="font-medium mb-2 block">{t('Feedback Type')}</label>
                                <Dropdown
                                    value={entryForm.feedbackType}
                                    options={localizedFeedbackTypeOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => onFieldChange('feedbackType', e.value)}
                                    className="w-full"
                                />
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="font-medium mb-2 block">{t('Employee')}</label>
                                <Dropdown
                                    value={entryForm.targetEmployee}
                                    options={employeeOptions}
                                    onChange={(e) => onFieldChange('targetEmployee', e.value)}
                                    placeholder={t('Select employee')}
                                    filter
                                    className="w-full"
                                />
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="font-medium mb-2 block">{t('Category')}</label>
                                <Dropdown
                                    value={entryForm.feedbackCategory}
                                    options={feedbackCategoryOptions}
                                    onChange={(e) => onFieldChange('feedbackCategory', e.value)}
                                    placeholder={t('Select feedback category')}
                                    className="w-full"
                                />
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="font-medium mb-2 block">{t('Location')}</label>
                                <InputText value={entryForm.location} onChange={(e) => onFieldChange('location', e.target.value)} placeholder={t('Optional location')} className="w-full" />
                            </div>
                            <div className="col-12">
                                <div className="flex align-items-center gap-2">
                                    <Checkbox
                                        inputId="hrConfidentialSubmission"
                                        checked={Boolean(entryForm.isConfidentialSubmission)}
                                        onChange={(e) => onFieldChange('isConfidentialSubmission', e.checked)}
                                    />
                                    <label htmlFor="hrConfidentialSubmission" className="font-medium">
                                        {t('Confidential Submission')}
                                    </label>
                                </div>
                            </div>
                            {entryForm.feedbackType === 'complaint' && (
                                <div className="col-12 md:col-6">
                                    <label className="font-medium mb-2 block">{t('Severity')}</label>
                                    <Dropdown value={entryForm.severity} options={localizedSeverityOptions} optionLabel="label" optionValue="value" onChange={(e) => onFieldChange('severity', e.value)} className="w-full" />
                                </div>
                            )}
                            <div className="col-12">
                                <div className="surface-ground border-round-lg px-3 py-2">
                                    <span className="text-600 text-sm">{t('Routing Preview: ')}</span>
                                    <span className="text-900 font-medium">{selectedFeedbackRouting ? t(selectedFeedbackRouting) : t('Select feedback type')}</span>
                                    {selectedFeedbackRoutingSetting?.assignedUsers?.length ? (
                                        <div className="text-700 text-sm mt-1">{t('Route Owners')}: {routeOwnersText(selectedFeedbackRoutingSetting.assignedUsers)}</div>
                                    ) : null}
                                </div>
                            </div>
                        </>
                    )}

                    <div className="col-12">
                        <label className="font-medium mb-2 block">{t('Photos')}</label>
                        <PhotoUploader
                            value=""
                            onChange={addEntryPhoto}
                            maxUploadBytes={500 * 1024}
                            maxDimension={1280}
                            helperText={t(`Optional. Up to ${MAX_HR_PHOTOS} photos. Max 500 KB each.`)}
                        />
                        {entryForm.photoUrls.length > 0 && (
                            <div className="mt-3">
                                <small className="text-500 block mb-2">{entryForm.photoUrls.length} {entryForm.photoUrls.length !== 1 ? t('photos') : t('photo')} {t('added')}</small>
                                <div className="grid">
                                    {entryForm.photoUrls.map((photo, index) => (
                                        <div className="col-12 md:col-4" key={`hr-entry-photo-${index}`}>
                                            <div style={{ border: '1px solid rgba(148,163,184,0.4)', borderRadius: '6px', padding: '0.5rem' }}>
                                                <img
                                                    src={photo}
                                                    alt={`HR upload ${index + 1}`}
                                                    style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '4px' }}
                                                />
                                                <div className="flex justify-content-end mt-2">
                                                    <Button
                                                        type="button"
                                                        label={t('Remove')}
                                                        icon="pi pi-times"
                                                        className="p-button-danger p-button-text p-button-sm"
                                                        onClick={() => removeEntryPhoto(index)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="col-12">
                        <label className="font-medium mb-2 block">{t('Description')}</label>
                        <InputTextarea value={entryForm.description} onChange={(e) => onFieldChange('description', e.target.value)} rows={5} className="w-full" placeholder={t(isSyncroRequest ? 'Describe what happened, what you expected, and the affected Syncro screen or module' : 'Describe the request or feedback')} />
                    </div>
                </div>

                <div className="flex justify-content-end gap-2 mt-4">
                    <Button label={t('Cancel')} className="p-button-text" onClick={closeEntryDialog} />
                    <Button label={submitting ? t('Saving...') : t('Save')} onClick={submitEntry} disabled={entryDialogDisabled || submitting} />
                </div>
            </Dialog>

            <Dialog
                header={
                    detailDialog.type === 'request'
                        ? t(detailDialog?.data?.subcategory || 'Request Detail')
                        : t(detailDialog?.data?.category || 'Feedback Detail')
                }
                visible={detailDialog.visible}
                onHide={closeTicketDetail}
                style={{ width: 'min(96vw, 70rem)' }}
            >
                {detailDialog?.data ? (
                    <>
                        <div className="grid mb-3">
                            <div className="col-12 md:col-4">
                                <div className="surface-ground border-round-lg p-3 h-full">
                                    <div className="text-600 text-sm mb-1">{t('Created')}</div>
                                    <div className="text-900 font-medium">{formatDateTime(detailDialog.data.createdAt)}</div>
                                </div>
                            </div>
                            <div className="col-12 md:col-4">
                                <div className="surface-ground border-round-lg p-3 h-full">
                                    <div className="text-600 text-sm mb-1">{t('Routing')}</div>
                                    <div className="text-900 font-medium">{t(detailDialog.data.routingLabel || '-')}</div>
                                    <div className="text-600 text-sm mt-2">{t('Route Owners')}</div>
                                    <div className="text-900">{routeOwnersText(detailDialog.data.routeOwners)}</div>
                                </div>
                            </div>
                            <div className="col-12 md:col-4">
                                <div className="surface-ground border-round-lg p-3 h-full">
                                    <div className="text-600 text-sm mb-1">{t('Created By')}</div>
                                    <div className="text-900">{detailDialog.type === 'feedback' ? feedbackLoggedByTemplate(detailDialog.data) : personTemplate(detailDialog.data, 'createdBy')}</div>
                                    {detailDialog.type === 'request' ? (
                                        <>
                                            <div className="text-600 text-sm mt-2">{t('Closed By')}</div>
                                            <div className="text-900">{personTemplate(detailDialog.data, 'closedBy')}</div>
                                        </>
                                    ) : null}
                                    <div className="text-600 text-sm mt-2">{t('Closed At')}</div>
                                    <div className="text-900">{formatDateTime(detailDialog.data.closedAt)}</div>
                                    {detailDialog.type === 'request' ? (
                                        <>
                                            <div className="text-600 text-sm mt-2">{t('Time Elapsed')}</div>
                                            <div className="text-900">{elapsedTimeTemplate(detailDialog.data)}</div>
                                        </>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        <div className="grid mb-3">
                            <div className="col-12 md:col-6">
                                <label className="font-medium mb-2 block">{t('Status')}</label>
                                <div className="surface-ground border-round-lg px-3 py-3 text-900">
                                    {statusTagTemplate(detailDialog?.data?.status)}
                                </div>
                            </div>
                            <div className="col-12 md:col-6">
                                <label className="font-medium mb-2 block">
                                    {detailDialog.type === 'request' ? t('Priority') : t('Severity')}
                                </label>
                                {detailDialog.type === 'request' ? (
                                    <div className="surface-ground border-round-lg px-3 py-3 text-900">
                                        {priorityTagTemplate(detailDialog?.data?.priority)}
                                    </div>
                                ) : detailDialog?.data?.type === 'complaint' ? (
                                    <Dropdown
                                        value={detailDialog?.data?.severity}
                                        options={localizedSeverityOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => updateFeedbackField(detailDialog.data, 'severity', e.value)}
                                        className="w-full"
                                    />
                                ) : (
                                    <div className="surface-ground border-round-lg px-3 py-3 text-700">{t('Not applicable for recognition')}</div>
                                )}
                            </div>
                        </div>

                        {detailDialog.type === 'request' ? (
                            <div className="mb-3">
                                <label htmlFor="hr-detail-assignee" className="font-medium mb-2 block">{t('Assigned To')}</label>
                                <Dropdown
                                    inputId="hr-detail-assignee"
                                    value={detailDialog.data?.assignedTo?._id || detailDialog.data?.assignedTo || null}
                                    options={requestAssigneeOptionsFor(detailDialog.data)}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => updateRequestAssignee(detailDialog.data, e.value)}
                                    placeholder={t('Select responsible')}
                                    emptyMessage={t('No route owners configured')}
                                    className="w-full"
                                    showClear
                                />
                            </div>
                        ) : null}

                        <div className="grid mb-3">
                            {detailDialog.type === 'request' ? (
                                <>
                                    <div className="col-12 md:col-6">
                                        <div className="surface-ground border-round-lg p-3 h-full">
                                            <div className="text-600 text-sm mb-1">{t('Category')}</div>
                                            <div className="text-900 font-medium">{t(detailDialog.data.category || '-')}</div>
                                            <div className="text-600 text-sm mt-3 mb-1">{t('Subcategory')}</div>
                                            <div className="text-900">{t(detailDialog.data.subcategory || '-')}</div>
                                            <div className="text-600 text-sm mt-3 mb-1">{t('Requested For')}</div>
                                            <div className="text-900">{personTemplate(detailDialog.data, 'requestedFor')}</div>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="surface-ground border-round-lg p-3 h-full">
                                            <div className="text-600 text-sm mb-1">{t('Location')}</div>
                                            <div className="text-900">{detailDialog.data.location || '-'}</div>
                                            <div className="text-600 text-sm mt-3 mb-1">{t('Machine')}</div>
                                            <div className="text-900">{machineTemplate(detailDialog.data)}</div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="col-12 md:col-6">
                                        <div className="surface-ground border-round-lg p-3 h-full">
                                            <div className="text-600 text-sm mb-1">{t('Employee')}</div>
                                            <div className="text-900 font-medium">{personTemplate(detailDialog.data, 'targetEmployee')}</div>
                                            <div className="text-600 text-sm mt-3 mb-1">{t('Type')}</div>
                                            <div className="text-900">
                                                <Tag value={translateHrToken(detailDialog.data.type, preferredLanguage)} severity={statusSeverity(detailDialog.data.type)} />
                                            </div>
                                            <div className="text-600 text-sm mt-3 mb-1">{t('Category')}</div>
                                            <div className="text-900">{t(detailDialog.data.category || '-')}</div>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <div className="surface-ground border-round-lg p-3 h-full">
                                            <div className="text-600 text-sm mb-1">{t('Location')}</div>
                                            <div className="text-900">{detailDialog.data.location || '-'}</div>
                                            <div className="text-600 text-sm mt-3 mb-1">{t('Confidential')}</div>
                                            <div className="text-900">
                                                {detailDialog.data.isConfidentialSubmission ? t('Confidential Submission') : t('Named Submission')}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {renderDetailModule(isAdmin)}

                        <div className="mb-4">
                            <label className="font-medium mb-2 block">{t('Description')}</label>
                            <div className="surface-ground border-round-lg p-3 text-900" style={{ whiteSpace: 'pre-wrap' }}>
                                {detailDialog.data.description || '-'}
                            </div>
                        </div>

                        {Array.isArray(detailDialog.data.photoUrls) && detailDialog.data.photoUrls.length > 0 ? (
                            <div className="mb-4">
                                <div className="flex justify-content-between align-items-center mb-2">
                                    <h4 className="m-0">{t('Photos')}</h4>
                                    <span className="text-600 text-sm">
                                        {detailDialog.data.photoUrls.length} {detailDialog.data.photoUrls.length !== 1 ? t('photos') : t('photo')}
                                    </span>
                                </div>
                                <div className="grid">
                                    {detailDialog.data.photoUrls.map((photo, index) => (
                                        <div className="col-12 md:col-4" key={`hr-detail-photo-${index}`}>
                                            <div style={{ border: '1px solid rgba(148,163,184,0.4)', borderRadius: '6px', padding: '0.5rem' }}>
                                                <img
                                                    src={photo}
                                                    alt={`Ticket attachment ${index + 1}`}
                                                    style={{ width: '100%', maxHeight: '180px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }}
                                                    onClick={() => window.open(photo, '_blank', 'noopener,noreferrer')}
                                                    title={t('Open')}
                                                />
                                                <div className="flex justify-content-end mt-2">
                                                    <Button
                                                        type="button"
                                                        label={t('View')}
                                                        icon="pi pi-search-plus"
                                                        className="p-button-text p-button-sm"
                                                        onClick={() => window.open(photo, '_blank', 'noopener,noreferrer')}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        <div className="mb-3">
                            <div className="flex justify-content-between align-items-center mb-2">
                                <h4 className="m-0">{t('Internal Comments')}</h4>
                                <span className="text-600 text-sm">
                                    {detailComments.length} {detailComments.length === 1 ? t('comment') : t('comments')}
                                </span>
                            </div>
                            <div className="grid">
                                <div className="col-12 md:col-8">
                                    <InputTextarea
                                        value={detailComment}
                                        onChange={(e) => setDetailComment(e.target.value)}
                                        rows={3}
                                        className="w-full"
                                        placeholder={t('Add an internal comment')}
                                    />
                                </div>
                                <div className="col-12 md:col-4 flex align-items-end">
                                    <Button
                                        label={t('Add Comment')}
                                        className="w-full"
                                        onClick={addDetailComment}
                                        disabled={!String(detailComment || '').trim()}
                                    />
                                </div>
                            </div>
                            <div className="mt-3">
                                {detailComments.length ? (
                                    detailComments.map((item) => (
                                        <div key={item._id || `${item.createdAt}-${item.comment}`} className="surface-ground border-round-lg p-3 mb-2">
                                            <div className="flex justify-content-between gap-2 flex-wrap mb-2">
                                                <span className="font-medium text-900">{formatUserName(item.createdBy)}</span>
                                                <span className="text-600 text-sm">{formatDateTime(item.createdAt)}</span>
                                            </div>
                                            <div className="text-900" style={{ whiteSpace: 'pre-wrap' }}>{item.comment}</div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="surface-ground border-round-lg p-3 text-600">{t('No comments yet.')}</div>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-content-end gap-2 flex-wrap">
                            <Button
                                label={t('Mark Open')}
                                className="p-button-outlined"
                                onClick={() => updateDetailStatus('open')}
                                disabled={detailDialog?.data?.status === 'open'}
                            />
                            <Button
                                label={t('Mark In Progress')}
                                className="p-button-warning"
                                onClick={() => updateDetailStatus('in_progress')}
                                disabled={
                                    detailDialog.type === 'request'
                                        ? detailDialog?.data?.status === 'in_progress'
                                        : detailDialog?.data?.status === 'in_review'
                                }
                            />
                            <Button
                                label={t('Close Ticket')}
                                className="p-button-success"
                                onClick={() => updateDetailStatus('resolved')}
                                disabled={detailDialog?.data?.status === 'resolved'}
                            />
                        </div>
                    </>
                ) : null}
            </Dialog>

            <Dialog
                header={employeeProfile?.employee ? `${employeeProfile.employee.firstName || ''} ${employeeProfile.employee.lastName || ''}`.trim() : t('Employee Profile')}
                visible={profileDialog}
                onHide={() => setProfileDialog(false)}
                style={{ width: 'min(96vw, 72rem)' }}
            >
                {employeeProfile ? (
                    <>
                        <div className="grid mb-3">
                            <div className="col-12 md:col-3">
                                <div className="surface-ground border-round-lg p-3 h-full">
                                    <div className="text-600 text-sm mb-1">{t('Score')}</div>
                                    <div className="text-900 text-3xl font-bold">{employeeProfile.summary?.score || 0}</div>
                                </div>
                            </div>
                            <div className="col-12 md:col-3">
                                <div className="surface-ground border-round-lg p-3 h-full">
                                    <div className="text-600 text-sm mb-1">{t('Recognitions')}</div>
                                    <div className="text-900 text-3xl font-bold">{employeeProfile.summary?.recognitions || 0}</div>
                                </div>
                            </div>
                            <div className="col-12 md:col-3">
                                <div className="surface-ground border-round-lg p-3 h-full">
                                    <div className="text-600 text-sm mb-1">{t('Complaints')}</div>
                                    <div className="text-900 text-3xl font-bold">{employeeProfile.summary?.complaints || 0}</div>
                                </div>
                            </div>
                            <div className="col-12 md:col-3">
                                <div className="surface-ground border-round-lg p-3 h-full">
                                    <div className="text-600 text-sm mb-1">{t('Assigned Requests')}</div>
                                    <div className="text-900 text-3xl font-bold">{employeeProfile.summary?.assignedRequests || 0}</div>
                                </div>
                            </div>
                            <div className="col-12 md:col-3">
                                <div className="surface-ground border-round-lg p-3 h-full">
                                    <div className="text-600 text-sm mb-1">{t('Alert')}</div>
                                    <div className="mt-2">
                                        {employeeProfile.summary?.escalationTriggered ? <Tag value={t('3+ complaints in 7 days')} severity="danger" /> : <Tag value={t('No active escalation')} severity="success" />}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <h4 className="mb-2">{t('Timeline')}</h4>
                            <DataTable value={employeeProfile.timeline || []} paginator rows={8} emptyMessage={t('No timeline entries yet.')}>
                                <Column field="createdAt" header={t('Date')} body={(row) => formatDateTime(row.createdAt)} />
                                <Column
                                    field="entryType"
                                    header={t('Type')}
                                    body={(row) => <Tag value={translateHrToken(row.entryType, preferredLanguage)} severity={row.entryType === 'feedback' ? 'warning' : 'info'} />}
                                />
                                <Column field="title" header={t('Title')} body={(row) => t(row.title || '-')} style={{ minWidth: '16rem' }} />
                                <Column field="subtitle" header={t('Category')} body={(row) => t(row.subtitle || '-')} style={{ minWidth: '14rem' }} />
                                <Column
                                    field="status"
                                    header={t('Status')}
                                    body={(row) => <Tag value={translateHrToken(row.status || '-', preferredLanguage)} severity={statusSeverity(row.status)} />}
                                />
                                <Column field="routingLabel" header={t('Routing')} body={(row) => t(row.routingLabel || '-')} style={{ minWidth: '14rem' }} />
                                <Column header={t('Route Owners')} body={(row) => routeOwnersTemplate(row.payload || row)} style={{ minWidth: '14rem' }} />
                                <Column header={t('Assigned To')} body={(row) => personTemplate(row, 'assignedTo')} />
                                <Column field="description" header={t('Description')} style={{ minWidth: '18rem' }} />
                            </DataTable>
                        </div>
                    </>
                ) : null}
            </Dialog>
        </div>
    );
};

export default HRModule;
